import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { 
  X, ZoomIn, ZoomOut, RotateCw, Maximize2, Minimize2, 
  Download, Printer, ChevronLeft, ChevronRight, 
  FileText, Loader2, AlertCircle, RefreshCw, ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

// ============================================
// NAPRAWIONA KONFIGURACJA WORKERA PDF.js
// ============================================
// Dynamicznie zaciągamy wersję zgodną z zainstalowaną paczką react-pdf
const PDFJS_VERSION = pdfjs.version;
const PDF_VIEWER_OPTIONS = { cMapUrl: `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/cmaps/`, cMapPacked: true, standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/standard_fonts/` };
const PDF_OPTIONS = { cMapUrl: `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/cmaps/`, cMapPacked: true };
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;

// Import styles for react-pdf
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// ============================================
// Helper Functions
// ============================================

// Get auth token from localStorage
const getAuthToken = () => {
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
};

// Build full URL from relative path
const buildFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${window.location.origin}${url}`;
};

// ============================================
// Main PDFViewer Component
// ============================================
export const PDFViewer = ({ 
  url, 
  filename = 'document.pdf',
  isOpen, 
  onClose,
  isMobile = false,
  sideBySide = false
}) => {
  // PDF state
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [fetchingPdf, setFetchingPdf] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  
  // Blob URL for authenticated file access
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  
  // Fallback mode - use native iframe instead of react-pdf
  const [useFallback, setUseFallback] = useState(false);
  const [reactPdfFailed, setReactPdfFailed] = useState(false);
  
  // Container dimensions
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);
  const documentRef = useRef(null);
  
  // Touch/pinch zoom state
  const [touchStartDistance, setTouchStartDistance] = useState(null);
  const [initialScale, setInitialScale] = useState(1.0);

  // ============================================
  // Fetch PDF with Authorization
  // ============================================
  const fetchPdfWithAuth = useCallback(async () => {
    if (!url || !isOpen) return;
    
    setFetchingPdf(true);
    setLoading(true);
    setError(null);
    setErrorDetails(null);
    setReactPdfFailed(false);
    
    // Cleanup previous blob URL to prevent memory leaks
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    
    try {
      const fullUrl = buildFullUrl(url);
      console.log('[PDFViewer] Fetching PDF:', fullUrl);
      
      // Prepare headers with authorization token
      const headers = {
        'Accept': 'application/pdf, application/octet-stream, */*'
      };
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Fetch the PDF file
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers,
        credentials: 'include'
      });
      
      console.log('[PDFViewer] Response status:', response.status, response.statusText);
      
      // Check response status
      if (!response.ok) {
        const statusText = response.statusText || 'Unknown error';
        
        if (response.status === 401) {
          throw new Error('Sesja wygasła - zaloguj się ponownie (401)');
        } else if (response.status === 403) {
          throw new Error('Brak uprawnień do tego pliku (403)');
        } else if (response.status === 404) {
          throw new Error('Plik nie został znaleziony (404)');
        } else {
          throw new Error(`Błąd serwera: ${response.status} ${statusText}`);
        }
      }
      
      // Check content type
      const contentType = response.headers.get('content-type') || '';
      console.log('[PDFViewer] Content-Type:', contentType);
      
      // If server returned HTML or JSON error instead of PDF
      if (contentType.includes('text/html') || contentType.includes('application/json')) {
        const text = await response.text();
        console.error('[PDFViewer] Server returned non-PDF content:', text.substring(0, 500));
        throw new Error('Serwer zwrócił błędną odpowiedź (nie PDF)');
      }
      
      // Convert response to Blob
      const blob = await response.blob();
      
      // Validate blob
      if (!blob || blob.size === 0) {
        throw new Error('Otrzymano pusty plik');
      }
      
      // Ensure correct MIME type for blob
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      
      console.log('[PDFViewer] PDF blob created:', { 
        size: pdfBlob.size, 
        type: pdfBlob.type 
      });
      
      // Create Object URL from Blob
      const blobUrl = URL.createObjectURL(pdfBlob);
      setPdfBlobUrl(blobUrl);
      
    } catch (err) {
      console.error('[PDFViewer] Fetch error:', err);
      
      let errorMessage = 'Nie można załadować dokumentu PDF';
      let details = err.message || '';
      
      if (err.message?.includes('401') || err.message?.includes('Sesja')) {
        errorMessage = 'Sesja wygasła';
        details = 'Zaloguj się ponownie, aby przeglądać dokumenty';
      } else if (err.message?.includes('403') || err.message?.includes('uprawnień')) {
        errorMessage = 'Brak dostępu';
        details = 'Nie masz uprawnień do przeglądania tego dokumentu';
      } else if (err.message?.includes('404') || err.message?.includes('znaleziony')) {
        errorMessage = 'Plik nie znaleziony';
        details = 'Dokument mógł zostać usunięty lub przeniesiony';
      } else if (err.name === 'TypeError' || err.message?.includes('network') || err.message?.includes('fetch')) {
        errorMessage = 'Błąd połączenia';
        details = 'Sprawdź połączenie internetowe i spróbuj ponownie';
      }
      
      setError(errorMessage);
      setErrorDetails(details);
      setLoading(false);
    } finally {
      setFetchingPdf(false);
    }
  }, [url, isOpen]);

  // ============================================
  // Effects
  // ============================================
  
  // Fetch PDF when component opens
  useEffect(() => {
    if (isOpen && url) {
      fetchPdfWithAuth();
    }
    
    // Cleanup on unmount
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [isOpen, url]);

  // Update container width on resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth - 40);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevPage();
          break;
        case 'ArrowRight':
          goToNextPage();
          break;
        case 'Escape':
          onClose();
          break;
        case '+':
        case '=':
          if (e.ctrlKey) {
            e.preventDefault();
            zoomIn();
          }
          break;
        case '-':
          if (e.ctrlKey) {
            e.preventDefault();
            zoomOut();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, numPages, onClose]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // ============================================
  // Document Handlers
  // ============================================
  
  const onDocumentLoadSuccess = ({ numPages }) => {
    console.log('[PDFViewer] Document loaded successfully:', numPages, 'pages');
    setNumPages(numPages);
    setLoading(false);
    setError(null);
    setReactPdfFailed(false);
  };

  const onDocumentLoadError = (err) => {
    console.error('[PDFViewer] react-pdf error:', err);
    
    // If react-pdf fails, switch to fallback iframe mode
    if (!useFallback && pdfBlobUrl) {
      console.log('[PDFViewer] Switching to iframe fallback mode');
      setReactPdfFailed(true);
      setUseFallback(true);
      setLoading(false);
      return;
    }
    
    let errorMessage = 'Błąd inicjalizacji';
    let details = 'Problem z biblioteką PDF';
    
    if (err?.message?.includes('worker')) {
      errorMessage = 'Błąd inicjalizacji';
      details = 'Problem z ładowaniem workera PDF.js';
    } else if (err?.message?.includes('Invalid') || err?.message?.includes('corrupt')) {
      errorMessage = 'Uszkodzony plik PDF';
      details = 'Plik jest nieprawidłowy lub uszkodzony';
    } else if (err?.message?.includes('version')) {
      errorMessage = 'Błąd wersji';
      details = 'Niezgodność wersji biblioteki PDF';
    }
    
    setError(errorMessage);
    setErrorDetails(details);
    setLoading(false);
  };

  // ============================================
  // Navigation & Zoom Functions
  // ============================================
  
  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages || 1));
  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
  const fitToWidth = () => {
    if (containerWidth > 0) {
      setScale(containerWidth / 612);
    }
  };
  const rotate = () => setRotation(prev => (prev + 90) % 360);

  // ============================================
  // Print & Download
  // ============================================
  
  const handlePrint = () => {
    if (pdfBlobUrl) {
      const printWindow = window.open(pdfBlobUrl);
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
    }
  };

  const handleDownload = () => {
    if (pdfBlobUrl) {
      const link = document.createElement('a');
      link.href = pdfBlobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOpenInNewTab = () => {
    if (pdfBlobUrl) {
      window.open(pdfBlobUrl, '_blank');
    }
  };

  // ============================================
  // Fullscreen
  // ============================================
  
  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // ============================================
  // Touch Handlers (Pinch Zoom)
  // ============================================
  
  const getTouchDistance = (touches) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      setTouchStartDistance(getTouchDistance(e.touches));
      setInitialScale(scale);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && touchStartDistance) {
      const currentDistance = getTouchDistance(e.touches);
      const scaleChange = currentDistance / touchStartDistance;
      setScale(Math.min(Math.max(initialScale * scaleChange, 0.5), 3));
    }
  };

  const handleTouchEnd = () => setTouchStartDistance(null);

  // ============================================
  // Retry Handler
  // ============================================
  
  const handleRetry = () => {
    setUseFallback(false);
    setReactPdfFailed(false);
    fetchPdfWithAuth();
  };

  // Don't render if not open
  if (!isOpen) return null;

  // ============================================
  // Render
  // ============================================
  return (
    <div 
      className="fixed inset-0 z-50 bg-black/90 flex flex-col"
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* ============================================ */}
      {/* Toolbar */}
      {/* ============================================ */}
      <div className="bg-[var(--crm-bg-main)] border-b border-[var(--crm-border)] px-4 py-2 flex items-center justify-between flex-shrink-0">
        {/* Left - filename and navigation */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-white">
            <FileText className="w-5 h-5 text-[#FF6B35]" />
            <span className="text-sm font-medium truncate max-w-[150px] md:max-w-[300px]">
              {filename}
            </span>
            {useFallback && (
              <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                Tryb awaryjny
              </span>
            )}
          </div>
          
          {!useFallback && numPages && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                className="h-8 w-8 p-0 text-[var(--crm-text-secondary)] hover:text-white hover:bg-[var(--crm-bg-btn-secondary)]"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-[var(--crm-text-secondary)] text-sm min-w-[60px] text-center">
                {pageNumber} / {numPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                className="h-8 w-8 p-0 text-[var(--crm-text-secondary)] hover:text-white hover:bg-[var(--crm-bg-btn-secondary)]"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Center - zoom controls (not in fallback mode) */}
        {!useFallback && (
          <div className="hidden md:flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={zoomOut}
              disabled={scale <= 0.5}
              className="h-9 w-9 p-0 text-[var(--crm-text-secondary)] hover:text-white hover:bg-[var(--crm-bg-btn-secondary)]"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-[var(--crm-text-secondary)] text-sm min-w-[50px] text-center">
              {Math.round(scale * 100)}%
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={zoomIn}
              disabled={scale >= 3}
              className="h-9 w-9 p-0 text-[var(--crm-text-secondary)] hover:text-white hover:bg-[var(--crm-bg-btn-secondary)]"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={fitToWidth}
              className="h-9 px-3 text-[var(--crm-text-secondary)] hover:text-white hover:bg-[var(--crm-bg-btn-secondary)]"
            >
              Dopasuj
            </Button>
          </div>
        )}

        {/* Right - actions */}
        <div className="flex items-center gap-1">
          {!useFallback && (
            <Button
              variant="ghost"
              size="sm"
              onClick={rotate}
              title="Obróć"
              className="h-9 w-9 p-0 text-[var(--crm-text-secondary)] hover:text-white hover:bg-[var(--crm-bg-btn-secondary)]"
            >
              <RotateCw className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Zamknij pełny ekran" : "Pełny ekran"}
            className="h-9 w-9 p-0 text-[var(--crm-text-secondary)] hover:text-white hover:bg-[var(--crm-bg-btn-secondary)]"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleOpenInNewTab}
            disabled={!pdfBlobUrl}
            title="Otwórz w nowej karcie"
            className="h-9 w-9 p-0 text-[var(--crm-text-secondary)] hover:text-white hover:bg-[var(--crm-bg-btn-secondary)]"
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
          {!isMobile && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrint}
                disabled={!pdfBlobUrl}
                title="Drukuj"
                className="h-9 w-9 p-0 text-[var(--crm-text-secondary)] hover:text-white hover:bg-[var(--crm-bg-btn-secondary)]"
              >
                <Printer className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                disabled={!pdfBlobUrl}
                title="Pobierz"
                className="h-9 w-9 p-0 text-[var(--crm-text-secondary)] hover:text-white hover:bg-[var(--crm-bg-btn-secondary)]"
              >
                <Download className="w-4 h-4" />
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            title="Zamknij (Esc)"
            className="h-9 w-9 p-0 text-[var(--crm-text-secondary)] hover:text-white hover:bg-[var(--crm-bg-btn-secondary)] ml-2"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* ============================================ */}
      {/* PDF Content Area */}
      {/* ============================================ */}
      <div 
        className="flex-1 overflow-auto flex items-start justify-center"
        ref={documentRef}
      >
        {/* Loading state */}
        {(loading || fetchingPdf) && !error && (
          <div className="flex items-center justify-center h-full w-full">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-[#FF6B35] animate-spin mx-auto mb-4" />
              <p className="text-[var(--crm-text-secondary)]">
                {fetchingPdf ? 'Pobieranie dokumentu...' : 'Ładowanie PDF...'}
              </p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && !useFallback && (
          <div className="flex items-center justify-center h-full w-full">
            <div className="text-center max-w-md px-4">
              <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <p className="text-red-300 text-lg mb-2">{error}</p>
              {errorDetails && (
                <p className="text-[var(--crm-text-muted)] text-sm mb-4">{errorDetails}</p>
              )}
              <div className="flex gap-2 justify-center">
                <Button 
                  onClick={handleRetry}
                  className="bg-[#FF6B35] hover:bg-[#e55a2a] flex items-center gap-2"
                  size="sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  Spróbuj ponownie
                </Button>
                {pdfBlobUrl && (
                  <Button 
                    onClick={() => setUseFallback(true)}
                    variant="outline"
                    className="border-[var(--crm-border)] text-[var(--crm-text-secondary)]"
                    size="sm"
                  >
                    Tryb awaryjny
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* FALLBACK MODE: Native iframe */}
        {/* ============================================ */}
        {useFallback && pdfBlobUrl && (
          <div className="w-full h-full p-4">
            <iframe
              src={pdfBlobUrl}
              width="100%"
              height="100%"
              style={{ border: 'none', borderRadius: '8px' }}
              title={`Podgląd: ${filename}`}
              type="application/pdf"
            />
          </div>
        )}

        {/* ============================================ */}
        {/* NORMAL MODE: react-pdf Document */}
        {/* ============================================ */}
        {!useFallback && pdfBlobUrl && !error && (
          <Document
            file={pdfBlobUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={null}
            className="py-4"
            options={PDF_VIEWER_OPTIONS}
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              rotate={rotation}
              width={containerWidth > 0 ? containerWidth : undefined}
              className="shadow-2xl"
              renderTextLayer={!isMobile}
              renderAnnotationLayer={!isMobile}
            />
          </Document>
        )}
      </div>

      {/* ============================================ */}
      {/* Mobile zoom hint */}
      {/* ============================================ */}
      {isMobile && !error && pdfBlobUrl && !useFallback && (
        <div className="bg-[var(--crm-bg-main)] border-t border-[var(--crm-border)] px-4 py-2 flex items-center justify-center gap-2 flex-shrink-0">
          <span className="text-[var(--crm-text-secondary)] text-xs">Użyj gestu szczypania, aby powiększyć/pomniejszyć</span>
        </div>
      )}
    </div>
  );
};

// ============================================
// PDFThumbnail Component
// ============================================
export const PDFThumbnail = ({ url, filename, className }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  // Fetch thumbnail with auth when hovered
  useEffect(() => {
    if (!isHovered || !url || blobUrl) return;
    
    const fetchThumbnail = async () => {
      try {
        const fullUrl = buildFullUrl(url);
        const headers = {};
        const token = getAuthToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(fullUrl, { headers });
        if (!response.ok) throw new Error('Failed to fetch thumbnail');
        
        const blob = await response.blob();
        const newBlobUrl = URL.createObjectURL(blob);
        setBlobUrl(newBlobUrl);
      } catch (err) {
        console.error('[PDFThumbnail] Error:', err);
        setError(true);
      }
    };
    
    fetchThumbnail();
    
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [isHovered, url, blobUrl]);

  if (!url) return null;

  const isPDF = url.toLowerCase().endsWith('.pdf') || filename?.toLowerCase().endsWith('.pdf');

  if (!isPDF) {
    return (
      <div className={cn("relative", className)}>
        <FileText className="w-5 h-5 text-[var(--crm-text-secondary)]" />
      </div>
    );
  }

  return (
    <div 
      className={cn("relative cursor-pointer", className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <FileText className="w-5 h-5 text-red-400" />
      
      {/* Hover preview */}
      {isHovered && !error && blobUrl && (
        <div className="absolute bottom-full left-0 mb-2 z-50 bg-[var(--crm-bg-main)] rounded-lg shadow-xl border border-[var(--crm-border)] p-2 hidden md:block">
          <div className="w-[150px] h-[200px] overflow-hidden">
            <Document
              file={blobUrl}
              onLoadSuccess={() => setThumbnailLoaded(true)}
              onLoadError={() => setError(true)}
              loading={
                <div className="w-full h-full flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-[#FF6B35] animate-spin" />
                </div>
              }
              options={PDF_OPTIONS}
            >
              <Page
                pageNumber={1}
                width={140}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFViewer;